import { useState, useEffect, useCallback } from 'react';
import analyticsAPI from '../utils/api';

// Data normalization utility
const normalizeAnalyticsData = (rawData, dailyData = null) => {
  if (!rawData) return null;

  // Helper function to calculate metrics from daily data
  const calculateMetricsFromDailyData = (dailyData) => {
    if (!dailyData || dailyData.length === 0) return null;
    
    const totals = dailyData.reduce((acc, day) => {
      const dayReach = day.summary?.totalReach || day.insights?.totalReach || day.reach || 0;
      const dayEngagements = day.summary?.totalEngagements || day.insights?.totalEngagements || day.engagements || 0;
      const dayPosts = day.posts?.length || day.insights?.totalPosts || day.postCount || 0;
      const dayNewFollowers = day.summary?.totalNewFollowers || day.insights?.totalNewFollowers || day.newFollowers || 0;
      const dayComments = day.comments?.length || day.insights?.totalComments || day.commentCount || 0;
      
      return {
        totalReach: acc.totalReach + dayReach,
        totalEngagements: acc.totalEngagements + dayEngagements,
        totalPosts: acc.totalPosts + dayPosts,
        totalNewFollowers: acc.totalNewFollowers + dayNewFollowers,
        totalComments: acc.totalComments + dayComments,
        days: acc.days + 1
      };
    }, { totalReach: 0, totalEngagements: 0, totalPosts: 0, totalNewFollowers: 0, totalComments: 0, days: 0 });
    
    return {
      totalReach: totals.totalReach,
      totalEngagements: totals.totalEngagements,
      totalPosts: totals.totalPosts,
      totalNewFollowers: totals.totalNewFollowers,
      totalComments: totals.totalComments,
      avgDailyReach: totals.days > 0 ? totals.totalReach / totals.days : 0,
      engagementRate: totals.totalReach > 0 ? (totals.totalEngagements / totals.totalReach) * 100 : 0,
      avgEngagementPerPost: totals.totalPosts > 0 ? totals.totalEngagements / totals.totalPosts : 0
    };
  };

  // Calculate metrics from daily data if available
  const calculatedMetrics = calculateMetricsFromDailyData(dailyData?.dataByDate || rawData.dataByDate || rawData.dailyData);

  // Aggregate totalUnfollows and retentionRate from daily data if available
  let aggregatedUnfollows = 0;
  let aggregatedNewFollowers = 0;
  if (Array.isArray(dailyData?.dataByDate || rawData.dataByDate || rawData.dailyData)) {
    const days = dailyData?.dataByDate || rawData.dataByDate || rawData.dailyData;
    aggregatedUnfollows = days.reduce((sum, day) => sum + Math.abs(day.summary?.totalUnfollows || day.insights?.totalUnfollows || day.unfollows || 0), 0);
    aggregatedNewFollowers = days.reduce((sum, day) => sum + (day.summary?.totalNewFollowers || day.insights?.totalNewFollowers || day.newFollowers || 0), 0);
  }
  // Retention rate: newFollowers / (newFollowers + unfollows)
  const aggregatedRetentionRate = (aggregatedNewFollowers + aggregatedUnfollows) > 0
    ? (aggregatedNewFollowers / (aggregatedNewFollowers + aggregatedUnfollows)) * 100
    : 0;

  // Calculate Growth Analysis metrics from daily data
  let growthTrend = '—';
  let growthConsistency = '—';
  let growthAcceleration = '—';
  let avgDailyGrowth = '—';
  if (Array.isArray(dailyData?.dataByDate || rawData.dataByDate || rawData.dailyData)) {
    const days = dailyData?.dataByDate || rawData.dataByDate || rawData.dailyData;
    // Calculate daily net growth
    const netGrowths = days.map(day => {
      const newF = day.summary?.totalNewFollowers || day.insights?.totalNewFollowers || day.newFollowers || 0;
      const unf = Math.abs(day.summary?.totalUnfollows || day.insights?.totalUnfollows || day.unfollows || 0);
      return newF - unf;
    });
    // Avg Daily Growth
    if (netGrowths.length > 0) {
      avgDailyGrowth = netGrowths.reduce((a, b) => a + b, 0) / netGrowths.length;
    }
    // Growth Consistency: % of days with positive net growth
    if (netGrowths.length > 0) {
      growthConsistency = (netGrowths.filter(n => n > 0).length / netGrowths.length) * 100;
    }
    // Growth Trend: slope of net growth (simple linear regression or just compare first/last)
    if (netGrowths.length > 1) {
      const first = netGrowths[0];
      const last = netGrowths[netGrowths.length - 1];
      if (last > first) growthTrend = 'increasing';
      else if (last < first) growthTrend = 'decreasing';
      else growthTrend = 'flat';
    }
    // Growth Acceleration: difference in avg net growth between first half and second half
    if (netGrowths.length > 3) {
      const half = Math.floor(netGrowths.length / 2);
      const avgFirst = netGrowths.slice(0, half).reduce((a, b) => a + b, 0) / half;
      const avgSecond = netGrowths.slice(half).reduce((a, b) => a + b, 0) / (netGrowths.length - half);
      growthAcceleration = avgSecond - avgFirst;
    }
  } else {
    // fallback to API values
    growthTrend = rawData.metrics?.growth?.growthTrend || rawData.overallMetrics?.growth?.growthTrend || '—';
    growthConsistency = rawData.metrics?.growth?.growthConsistency || rawData.overallMetrics?.growth?.growthConsistency || '—';
    growthAcceleration = rawData.metrics?.growth?.growthAcceleration || rawData.overallMetrics?.growth?.growthAcceleration || '—';
    avgDailyGrowth = rawData.metrics?.growth?.avgDailyGrowth || rawData.overallMetrics?.growth?.avgDailyGrowth || '—';
  }

  // Calculate Engagement Distribution from daily data
  let highEngagementPosts = '—';
  let mediumEngagementPosts = '—';
  let lowEngagementPosts = '—';
  let zeroEngagementPosts = '—';
  if (Array.isArray(dailyData?.dataByDate || rawData.dataByDate || rawData.dailyData)) {
    const days = dailyData?.dataByDate || rawData.dataByDate || rawData.dailyData;
    let allPosts = [];
    days.forEach(day => {
      if (Array.isArray(day.posts)) {
        allPosts = allPosts.concat(day.posts);
      }
    });
    // Calculate avg engagement per post
    const totalEngagements = allPosts.reduce((sum, post) => sum + (post.engagements || post.totalEngagements || 0), 0);
    const avgEngagement = allPosts.length > 0 ? totalEngagements / allPosts.length : 0;
    // Thresholds
    const highThresh = avgEngagement * 1.5;
    const medThresh = avgEngagement * 0.75;
    highEngagementPosts = allPosts.filter(post => (post.engagements || post.totalEngagements || 0) > highThresh).length;
    mediumEngagementPosts = allPosts.filter(post => {
      const e = post.engagements || post.totalEngagements || 0;
      return e > medThresh && e <= highThresh;
    }).length;
    lowEngagementPosts = allPosts.filter(post => {
      const e = post.engagements || post.totalEngagements || 0;
      return e > 0 && e <= medThresh;
    }).length;
    zeroEngagementPosts = allPosts.filter(post => (post.engagements || post.totalEngagements || 0) === 0).length;
  } else {
    highEngagementPosts = rawData.metrics?.engagement?.highEngagementPosts || rawData.overallMetrics?.engagement?.highEngagementPosts || '—';
    mediumEngagementPosts = rawData.metrics?.engagement?.mediumEngagementPosts || rawData.overallMetrics?.engagement?.mediumEngagementPosts || '—';
    lowEngagementPosts = rawData.metrics?.engagement?.lowEngagementPosts || rawData.overallMetrics?.engagement?.lowEngagementPosts || '—';
    zeroEngagementPosts = rawData.metrics?.engagement?.zeroEngagementPosts || rawData.overallMetrics?.engagement?.zeroEngagementPosts || '—';
  }

  // Normalize the data structure to be consistent
  const normalized = {
    period: {
      start: rawData.analysisPeriod?.start || rawData.startDate || '',
      end: rawData.analysisPeriod?.end || rawData.endDate || '',
      days: rawData.analysisPeriod?.days || rawData.totalDays || 0
    },
    summary: {
      totalPosts: calculatedMetrics?.totalPosts || rawData.summary?.totalPosts || rawData.overallSummary?.totalPosts || 0,
      totalReach: calculatedMetrics?.totalReach || rawData.summary?.totalReach || rawData.overallSummary?.totalReach || 0,
      totalEngagements: calculatedMetrics?.totalEngagements || rawData.summary?.totalEngagements || rawData.overallSummary?.totalEngagements || 0,
      engagementRate: calculatedMetrics?.engagementRate || rawData.summary?.engagementRate || rawData.overallSummary?.engagementRate || 0,
      totalDays: rawData.summary?.totalDays || rawData.overallSummary?.totalDays || 0,
      totalWeeks: rawData.summary?.totalWeeks || rawData.overallSummary?.totalWeeks || 0
    },
    metrics: {
      engagement: {
        totalEngagement: calculatedMetrics?.totalEngagements || rawData.metrics?.engagement?.totalEngagement || rawData.overallMetrics?.engagement?.totalEngagement || 0,
        totalPosts: calculatedMetrics?.totalPosts || rawData.metrics?.engagement?.totalPosts || rawData.overallMetrics?.engagement?.totalPosts || 0,
        engagementRate: calculatedMetrics?.engagementRate || rawData.metrics?.engagement?.engagementRate || rawData.overallMetrics?.engagement?.engagementRate || 0,
        totalComments: calculatedMetrics?.totalComments || rawData.metrics?.engagement?.totalComments || rawData.overallMetrics?.engagement?.totalComments || 0,
        avgEngagementPerPost: calculatedMetrics?.avgEngagementPerPost || rawData.metrics?.engagement?.avgEngagementPerPost || rawData.overallMetrics?.engagement?.avgEngagementPerPost || 0,
        highEngagementPosts,
        mediumEngagementPosts,
        lowEngagementPosts,
        zeroEngagementPosts,
      },
      growth: {
        totalNewFollowers: calculatedMetrics?.totalNewFollowers || rawData.metrics?.growth?.totalNewFollowers || rawData.overallMetrics?.growth?.totalNewFollowers || 0,
        totalUnfollows: aggregatedUnfollows || rawData.metrics?.growth?.totalUnfollows || rawData.overallMetrics?.growth?.totalUnfollows || 0,
        netGrowth: (calculatedMetrics?.totalNewFollowers || rawData.metrics?.growth?.totalNewFollowers || rawData.overallMetrics?.growth?.totalNewFollowers || 0)
                  - (aggregatedUnfollows || rawData.metrics?.growth?.totalUnfollows || rawData.overallMetrics?.growth?.totalUnfollows || 0),
        retentionRate: aggregatedRetentionRate || rawData.metrics?.growth?.retentionRate || rawData.overallMetrics?.growth?.retentionRate || 0,
        growthTrend,
        growthConsistency,
        growthAcceleration,
        avgDailyGrowth,
      },
      reach: {
        totalReach: calculatedMetrics?.totalReach || rawData.metrics?.reach?.totalReach || rawData.overallMetrics?.reach?.totalReach || 0,
        avgDailyReach: calculatedMetrics?.avgDailyReach || rawData.metrics?.reach?.avgDailyReach || rawData.overallMetrics?.reach?.avgDailyReach || 0
      }
    },
    analysis: {
      contentPerformance: rawData.analysis?.contentPerformance || rawData.contentPerformance || {},
      audienceMetrics: rawData.analysis?.audienceMetrics || rawData.audienceMetrics || {},
      trendsAndBenchmarks: rawData.analysis?.trendsAndBenchmarks || rawData.trendsAndBenchmarks || {},
      topPosts: rawData.analysis?.topPosts || rawData.topPosts || []
    },
    dailyData: dailyData?.dataByDate || rawData.dataByDate || rawData.dailyData || [],
    recommendations: rawData.recommendations || []
  };

  return normalized;
};

export const useAnalytics = (pageId, timeRange) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!pageId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch main analytics data
      let analyticsData;
      if (pageId === 'overall') {
        analyticsData = await analyticsAPI.getOverallAnalytics(timeRange);
      } else {
        analyticsData = await analyticsAPI.getPageAnalytics(pageId, timeRange);
      }

      // Fetch daily data for charts
      let dailyData = null;
      try {
        dailyData = await analyticsAPI.getClientData(pageId === 'overall' ? null : pageId, timeRange);
      } catch (dailyError) {
        console.warn('Could not fetch daily data:', dailyError);
        // Continue without daily data
      }

      // Normalize the data
      const normalizedData = normalizeAnalyticsData(analyticsData, dailyData);
      setData(normalizedData);

    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  }, [pageId, timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData
  };
};

export const usePages = () => {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPages = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const pagesData = await analyticsAPI.getPages();
      setPages(pagesData);
    } catch (err) {
      console.error('Error fetching pages:', err);
      setError(err.message || 'Failed to load pages');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  return {
    pages,
    isLoading,
    error,
    refetch: fetchPages
  };
}; 